import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridRow,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  SearchBox,
  Select,
  Skeleton,
  SkeletonItem,
  Spinner,
  TableCellLayout,
  Text,
  createTableColumn,
  makeStyles,
  tokens,
  type DataGridProps,
  type TableColumnDefinition,
  type TableColumnSizingOptions,
} from '@fluentui/react-components';
import { ArrowDownloadRegular, DiamondFilled, DismissRegular, EditRegular } from '@fluentui/react-icons';
import { usePdmAssignmentStyles } from '../../styles/PdmAssignmentPage.styles';
import { SummaryCardBar, type SummaryCardItem } from '../../components/SummaryCardBar';
import { CoverageBanner } from '../../components/pdm/CoverageBanner';
import { CoveragePill } from '../../components/pdm/CoveragePill';
import { RoleAssignmentCell } from '../../components/pdm/RoleAssignmentCell';
import { RegionalLeadsEditorPanel } from '../../components/pdm/RegionalLeadsEditorPanel';
import { usePdmAssignmentService } from '../../hooks/usePdmAssignmentService';
import { getTierColor } from '../../constants/tiers';
import { PDM_REGIONS, PDM_REGION_COLORS, NO_REGION_LABEL } from '../../constants/pdmRegions';
import { PDM_ROLE_LABELS, type PdmRole } from '../../constants/pdmRoleLabels';
import { compareString, compareTier } from '../../utils/comparators';
import { getFriendlyApiErrorMessage, logApiError } from '../../services/apiErrorMessages';
import { exportToCsv } from '../../utils/exportToCsv';
import { isNonEnglishName } from '../../utils/formatters';
import { trackEvent } from '../../services/telemetry';
import { usePartnerAliases } from '../../contexts/PartnerAliasContext';
import { PartnerAliasEditor } from '../../components/PartnerAliasEditor';
import {
  ROLE_TO_COLUMN,
  type PartnerAssignmentRow,
  type PdmAssignment,
  type PdmDirectoryEntry,
  type RegionalLeadGroup,
} from '../../models/pdmAssignment';
import { SEARCH_DEBOUNCE_MS } from '../../constants/table';

const ALL = 'All';
const UNASSIGNED_FILTER = '__unassigned__';

type AssignmentFilter = 'All' | 'Assigned' | 'Unassigned';

const COLUMN_IDS = [
  'partnerName', 'tier', 'region', 'regionalLeads', 'country', 'pdm', 'pdmManager',
] as const;
type PartnerColumnId = (typeof COLUMN_IDS)[number];

const COLUMN_SIZING: Record<PartnerColumnId, { minWidth: number; weight: number }> = {
  partnerName: { minWidth: 170, weight: 1.2 },
  tier:        { minWidth: 100, weight: 0.2 },
  region:      { minWidth: 90,  weight: 0.2 },
  regionalLeads: { minWidth: 140, weight: 0.8 },
  country:     { minWidth: 110, weight: 0.6 },
  pdm:         { minWidth: 200, weight: 1.2 },
  pdmManager:  { minWidth: 200, weight: 1.2 },
};

const firstName = (assignments: PdmAssignment[]): string =>
  assignments.length > 0 ? assignments[0].name : '';

const buildColumns = (
  renderRegionalHeader: () => React.ReactNode,
): TableColumnDefinition<PartnerAssignmentRow>[] => [
  createTableColumn<PartnerAssignmentRow>({
    columnId: 'partnerName',
    compare: (a, b) => compareString(a.partnerName, b.partnerName),
    renderHeaderCell: () => 'Partner name',
    renderCell: () => null,
  }),
  createTableColumn<PartnerAssignmentRow>({
    columnId: 'tier',
    compare: (a, b) => compareTier(a.tier, b.tier),
    renderHeaderCell: () => 'Tier',
    renderCell: () => null,
  }),
  createTableColumn<PartnerAssignmentRow>({
    columnId: 'region',
    compare: (a, b) => compareString(a.region, b.region),
    renderHeaderCell: () => 'Region',
    renderCell: () => null,
  }),
  createTableColumn<PartnerAssignmentRow>({
    columnId: 'regionalLeads',
    compare: (a, b) => compareString(firstName(a.regionalLeads), firstName(b.regionalLeads)),
    renderHeaderCell: () => renderRegionalHeader(),
    renderCell: () => null,
  }),
  createTableColumn<PartnerAssignmentRow>({
    columnId: 'country',
    compare: (a, b) => compareString(a.country, b.country),
    renderHeaderCell: () => 'Country',
    renderCell: () => null,
  }),
  createTableColumn<PartnerAssignmentRow>({
    columnId: 'pdm',
    compare: (a, b) => compareString(firstName(a.pdm), firstName(b.pdm)),
    renderHeaderCell: () => PDM_ROLE_LABELS.pdm,
    renderCell: () => null,
  }),
  createTableColumn<PartnerAssignmentRow>({
    columnId: 'pdmManager',
    compare: (a, b) => compareString(firstName(a.pdmManager), firstName(b.pdmManager)),
    renderHeaderCell: () => PDM_ROLE_LABELS.pdm_manager,
    renderCell: () => null,
  }),
];

interface PartnersTabProps {
  notify: (message: string, intent: 'success' | 'error') => void;
  canEdit: boolean;
}

const useAliasAffordanceStyles = makeStyles({
  container: {
    minWidth: 0,
    '& .alias-affordance': {
      opacity: 0,
      pointerEvents: 'none',
      transitionProperty: 'opacity',
      transitionDuration: tokens.durationFaster,
    },
    '&:hover .alias-affordance': {
      opacity: 1,
      pointerEvents: 'auto',
    },
    '&:focus-within .alias-affordance': {
      opacity: 1,
      pointerEvents: 'auto',
    },
  },
  affordance: {
    display: 'inline-flex',
    verticalAlign: 'middle',
    marginInlineStart: '2px',
  },
});

export const PartnersTab: React.FC<PartnersTabProps> = ({ notify, canEdit }) => {
  const styles = usePdmAssignmentStyles();
  const aliasStyles = useAliasAffordanceStyles();
  const { getAlias, getDisplayName } = usePartnerAliases();
  const service = usePdmAssignmentService();

  const [rows, setRows] = useState<PartnerAssignmentRow[]>([]);
  const [pdmPool, setPdmPool] = useState<PdmDirectoryEntry[]>([]);
  const [regionalLeadGroups, setRegionalLeadGroups] = useState<RegionalLeadGroup[]>([]);
  const [regionalLeadsError, setRegionalLeadsError] = useState<string | null>(null);
  const [regionalLeadsLoading, setRegionalLeadsLoading] = useState(false);
  const [regionalPanelOpen, setRegionalPanelOpen] = useState(false);
  const [mutatingRegion, setMutatingRegion] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [regionFilter, setRegionFilter] = useState<string>(ALL);
  const [pdmFilter, setPdmFilter] = useState<string>(ALL);
  const [assignmentFilter, setAssignmentFilter] = useState<AssignmentFilter>('All');
  const [mutatingKey, setMutatingKey] = useState<string | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  const reload = useCallback(async () => {
    if (!service) return;
    setIsLoading(true);
    setError(null);
    setRegionalLeadsError(null);
    try {
      const [assignments, pool] = await Promise.all([
        service.listAssignments(),
        canEdit
          ? service.listPdmPool().catch((err) => {
              logApiError('PartnersTab.listPdmPool', err);
              return [] as PdmDirectoryEntry[];
            })
          : Promise.resolve([] as PdmDirectoryEntry[]),
      ]);
      setRows(assignments);
      setPdmPool(pool);
      const groups = await service.listRegionalLeads().catch((err) => {
        logApiError('PartnersTab.listRegionalLeads', err);
        setRegionalLeadsError(getFriendlyApiErrorMessage(err));
        return null;
      });
      setRegionalLeadGroups(groups ?? []);
    } catch (err) {
      logApiError('PartnersTab.listAssignments', err);
      setError(getFriendlyApiErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [service, canEdit]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    const handle = setTimeout(() => setSearchQuery(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    trackEvent('pdm_page_view', { tab: 'partners' });
  }, []);

  // Region card counts always reflect the full set, not the filtered view (FR-10).
  const regionCards = useMemo<SummaryCardItem[]>(() => {
    const counts = new Map<string, number>();
    for (const r of rows) counts.set(r.region, (counts.get(r.region) ?? 0) + 1);
    return [
      { label: 'All', value: rows.length, accentColor: tokens.colorBrandBackground, filterValue: ALL },
      ...PDM_REGIONS.map((reg) => ({
        label: reg,
        value: counts.get(reg) ?? 0,
        accentColor: PDM_REGION_COLORS[reg],
        filterValue: reg,
      })),
    ];
  }, [rows]);

  const pdmFilterOptions = useMemo(() => {
    const byId = new Map<string, string>();
    for (const r of rows) {
      for (const a of r.pdm) byId.set(a.pdmId, a.name);
    }
    return [...byId.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);

  const filteredRows = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return rows.filter((r) => {
      if (regionFilter !== ALL && r.region !== regionFilter) return false;
      if (assignmentFilter === 'Assigned' && r.pdm.length === 0) return false;
      if (assignmentFilter === 'Unassigned' && r.pdm.length > 0) return false;
      if (pdmFilter === UNASSIGNED_FILTER) {
        if (r.pdm.length > 0) return false;
      } else if (pdmFilter !== ALL && !r.pdm.some((a) => a.pdmId === pdmFilter)) {
        return false;
      }
      if (q && !r.partnerName.toLowerCase().includes(q) && !(getAlias(r.partnerName)?.toLowerCase().includes(q) ?? false)) {
        return false;
      }
      return true;
    });
  }, [rows, regionFilter, assignmentFilter, pdmFilter, searchQuery, getAlias]);

  const hasActiveFilters =
    searchInput.trim() !== '' ||
    regionFilter !== ALL ||
    pdmFilter !== ALL ||
    assignmentFilter !== 'All';

  const clearFilters = useCallback(() => {
    setSearchInput('');
    setRegionFilter(ALL);
    setPdmFilter(ALL);
    setAssignmentFilter('All');
  }, []);

  const showUnassigned = useCallback(() => {
    setAssignmentFilter('Unassigned');
    trackEvent('pdm_coverage_banner_action', { action: 'show_unassigned' });
  }, []);

  const showUnassignedFromPill = useCallback(() => {
    setAssignmentFilter('Unassigned');
    trackEvent('pdm_coverage_pill_action', { action: 'show_unassigned' });
  }, []);

  const updateRowRole = useCallback(
    (partnerId: string, role: PdmRole, update: (list: PdmAssignment[]) => PdmAssignment[]) => {
      const column = ROLE_TO_COLUMN[role];
      setRows((prev) =>
        prev.map((r) => (r.partnerId === partnerId ? { ...r, [column]: update(r[column]) } : r)),
      );
    },
    [],
  );

  const handleAdd = useCallback(
    async (partner: PartnerAssignmentRow, role: PdmRole, person: PdmDirectoryEntry) => {
      if (!service) return;
      if (!person || !person.email) {
        notify('Selected PDM was not found in the directory. Please refresh and try again.', 'error');
        return;
      }
      const key = `${partner.partnerId}:${role}`;
      setMutatingKey(key);
      try {
        const created = await service.addAssignment({
          partnerId: partner.partnerId,
          pdmId: person.pdmId,
          role,
          pdmName: person.name,
          pdmEmail: person.email,
        });
        trackEvent('pdm_assignment_added', { role });
        notify(
          `${partner.partnerName}: ${person?.name ?? 'PDM'} assigned as ${PDM_ROLE_LABELS[role]}`,
          'success',
        );
        updateRowRole(partner.partnerId, role, (list) =>
          [...list.filter((a) => a.id !== created.id), created].sort((a, b) =>
            a.name.localeCompare(b.name),
          ),
        );
      } catch (err) {
        logApiError('PartnersTab.addAssignment', err);
        notify(getFriendlyApiErrorMessage(err), 'error');
      } finally {
        setMutatingKey(null);
      }
    },
    [service, notify, updateRowRole],
  );

  const searchDirectory = useCallback(
    (term: string): Promise<PdmDirectoryEntry[]> =>
      service ? service.listPdmPool(term) : Promise.resolve([]),
    [service],
  );

  const handleRemove = useCallback(
    async (partner: PartnerAssignmentRow, role: PdmRole, assignment: PdmAssignment) => {
      if (!service) return;
      const key = `${partner.partnerId}:${role}`;
      setMutatingKey(key);
      try {
        await service.removeAssignment(assignment.id, assignment.etag);
        trackEvent('pdm_assignment_removed', { role });
        notify(
          `${partner.partnerName}: ${assignment.name} removed from ${PDM_ROLE_LABELS[role]}`,
          'success',
        );
        updateRowRole(partner.partnerId, role, (list) =>
          list.filter((a) => a.id !== assignment.id),
        );
      } catch (err) {
        logApiError('PartnersTab.removeAssignment', err);
        notify(getFriendlyApiErrorMessage(err), 'error');
      } finally {
        setMutatingKey(null);
      }
    },
    [service, notify, updateRowRole],
  );

  const applyRegionalLeadGroups = useCallback((nextGroups: RegionalLeadGroup[]) => {
    setRegionalLeadGroups(nextGroups);
    const byRegion = new Map<string, PdmAssignment[]>();
    for (const g of nextGroups) byRegion.set(g.region.toLowerCase(), g.leads);
    setRows((prev) =>
      prev.map((r) => ({
        ...r,
        regionalLeads: r.region ? (byRegion.get(r.region.toLowerCase()) ?? []) : [],
      })),
    );
  }, []);

  const retryRegionalLeads = useCallback(async () => {
    if (!service) return;
    setRegionalLeadsLoading(true);
    try {
      const groups = await service.listRegionalLeads();
      setRegionalLeadsError(null);
      applyRegionalLeadGroups(groups);
    } catch (err) {
      logApiError('PartnersTab.retryRegionalLeads', err);
      setRegionalLeadsError(getFriendlyApiErrorMessage(err));
    } finally {
      setRegionalLeadsLoading(false);
    }
  }, [service, applyRegionalLeadGroups]);

  const handleAddRegionalLead = useCallback(
    async (region: string, person: PdmDirectoryEntry) => {
      if (!service) return;
      if (!person || !person.email) {
        notify('Selected person was not found in the directory. Please refresh and try again.', 'error');
        return;
      }
      setMutatingRegion(region);
      try {
        const created = await service.addRegionalLead(region, person.name, person.email);
        trackEvent('pdm_regional_lead_added', { region });
        notify(`${person.name} assigned as regional lead for ${region}`, 'success');
        const existing = regionalLeadGroups.find(
          (g) => g.region.toLowerCase() === region.toLowerCase(),
        );
        const leads = [...(existing?.leads ?? []).filter((l) => l.id !== created.id), created].sort(
          (a, b) => a.name.localeCompare(b.name),
        );
        applyRegionalLeadGroups(
          existing
            ? regionalLeadGroups.map((g) => (g === existing ? { ...g, leads } : g))
            : [...regionalLeadGroups, { region, leads }].sort((a, b) =>
                a.region.localeCompare(b.region),
              ),
        );
      } catch (err) {
        logApiError('PartnersTab.addRegionalLead', err);
        notify(getFriendlyApiErrorMessage(err), 'error');
      } finally {
        setMutatingRegion(null);
      }
    },
    [service, notify, regionalLeadGroups, applyRegionalLeadGroups],
  );

  const handleRemoveRegionalLead = useCallback(
    async (region: string, lead: PdmAssignment) => {
      if (!service) return;
      setMutatingRegion(region);
      try {
        await service.removeRegionalLead(lead.id, lead.etag);
        trackEvent('pdm_regional_lead_removed', { region });
        notify(`${lead.name} removed as regional lead for ${region}`, 'success');
        applyRegionalLeadGroups(
          regionalLeadGroups.map((g) =>
            g.region.toLowerCase() === region.toLowerCase()
              ? { ...g, leads: g.leads.filter((l) => l.id !== lead.id) }
              : g,
          ),
        );
      } catch (err) {
        logApiError('PartnersTab.removeRegionalLead', err);
        notify(getFriendlyApiErrorMessage(err), 'error');
      } finally {
        setMutatingRegion(null);
      }
    },
    [service, notify, regionalLeadGroups, applyRegionalLeadGroups],
  );

  const tableWrapperRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    const el = tableWrapperRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update);
      observer.observe(el);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const columnSizingOptions = useMemo<TableColumnSizingOptions>(() => {
    const totalMin = COLUMN_IDS.reduce((s, id) => s + COLUMN_SIZING[id].minWidth, 0);
    const totalWeight = COLUMN_IDS.reduce((s, id) => s + COLUMN_SIZING[id].weight, 0);
    const available = Math.max(containerWidth - 8, totalMin);
    const extra = Math.max(0, available - totalMin);
    const sizing: TableColumnSizingOptions = {};
    COLUMN_IDS.forEach((id) => {
      const { minWidth, weight } = COLUMN_SIZING[id];
      const ideal = totalWeight > 0 ? minWidth + Math.floor((extra * weight) / totalWeight) : minWidth;
      sizing[id] = { minWidth, idealWidth: ideal };
    });
    return sizing;
  }, [containerWidth]);

  const defaultSortState = useMemo<DataGridProps['defaultSortState']>(
    () => ({ sortColumn: 'partnerName', sortDirection: 'ascending' }),
    [],
  );

  const renderRoleCell = useCallback(
    (row: PartnerAssignmentRow, role: PdmRole) => {
      const key = `${row.partnerId}:${role}`;
      const assignments = row[ROLE_TO_COLUMN[role]];
      const rowAssignedIds = new Set(
        [...row.pdm, ...row.pdmManager, ...row.regionalLeads].map((a) => a.pdmId),
      );

      return (
        <RoleAssignmentCell
          role={role}
          assignments={assignments}
          pdmPool={pdmPool}
          rowAssignedIds={rowAssignedIds}
          onAdd={(entry) => handleAdd(row, role, entry)}
          onRemove={(assignment) => handleRemove(row, role, assignment)}
          onSearch={searchDirectory}
          disabled={!canEdit || (mutatingKey !== null && mutatingKey !== key)}
          readOnly={!canEdit}
          busy={mutatingKey === key}
        />
      );
    },
    [pdmPool, handleAdd, handleRemove, searchDirectory, canEdit, mutatingKey, styles],
  );

  const renderRegionalHeader = useCallback(
    () => (
      <span className={styles.regionalHeader}>
        {PDM_ROLE_LABELS.regional_leads}
        {canEdit && (
          <Button
            appearance="subtle"
            size="small"
            icon={<EditRegular />}
            className={styles.editRegionalLeadsButton}
            aria-label="Edit regional leads"
            title="Edit regional leads"
            onClick={(e) => {
              e.stopPropagation();
              setRegionalPanelOpen(true);
            }}
          >
            Edit
          </Button>
        )}
      </span>
    ),
    [styles, canEdit],
  );

  const columns = useMemo(
    () => buildColumns(renderRegionalHeader),
    [renderRegionalHeader],
  );

  const renderCellContent = useCallback(
    (columnId: string, row: PartnerAssignmentRow) => {
      switch (columnId as PartnerColumnId) {
        case 'partnerName': {
          const canAlias = isNonEnglishName(row.partnerName) || getAlias(row.partnerName) != null;
          return (
            <span className={aliasStyles.container}>
              {getDisplayName(row.partnerName)}
              {canAlias && (
                <span className={`alias-affordance ${aliasStyles.affordance}`}>
                  <PartnerAliasEditor originalName={row.partnerName} />
                </span>
              )}
            </span>
          );
        }
        case 'tier':
          return (
            <span className={styles.tierCell}>
              <DiamondFilled style={{ color: getTierColor(row.tier), fontSize: '16px' }} />
              {row.tier}
            </span>
          );
        case 'region':
          return row.region || NO_REGION_LABEL;
        case 'country':
          return row.country;
        case 'pdm':
          return renderRoleCell(row, 'pdm');
        case 'pdmManager':
          return renderRoleCell(row, 'pdm_manager');
        case 'regionalLeads':
          return row.regionalLeads.length > 0 ? (
            row.regionalLeads.map((l) => l.name).join(', ')
          ) : (
            <span className={styles.readOnlyEmpty}>—</span>
          );
        default:
          return null;
      }
    },
    [styles, renderRoleCell, getDisplayName, getAlias, aliasStyles],
  );

  const handleExport = useCallback(() => {
    trackEvent('pdm_export', { tab: 'partners', rows: filteredRows.length });
    const joinRole = (assignments: PdmAssignment[]) => assignments.map((a) => a.name).join('; ');
    exportToCsv<PartnerAssignmentRow>({
      fileName: 'pdm-partners',
      rows: filteredRows,
      columns: [
        { header: 'Partner Name', accessor: (r) => getDisplayName(r.partnerName) ?? '' },
        { header: 'Tier', accessor: (r) => r.tier },
        { header: 'Region', accessor: (r) => r.region || NO_REGION_LABEL },
        { header: PDM_ROLE_LABELS.regional_leads, accessor: (r) => joinRole(r.regionalLeads) },
        { header: 'Country', accessor: (r) => r.country },
        { header: PDM_ROLE_LABELS.pdm, accessor: (r) => joinRole(r.pdm) },
        { header: PDM_ROLE_LABELS.pdm_manager, accessor: (r) => joinRole(r.pdmManager) },
      ],
    });
  }, [filteredRows, getDisplayName]);

  return (
    <div className={styles.tabContent}>
      {error && (
        <div className={styles.messageBarWrapper}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Unable to load partners</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      <CoverageBanner
        rows={rows}
        dismissed={bannerDismissed}
        onDismiss={() => setBannerDismissed(true)}
        onShowUnassigned={showUnassigned}
      />

      <SummaryCardBar
        cards={regionCards}
        isLoading={isLoading && rows.length === 0}
        activeFilter={regionFilter}
        onCardClick={(value) => {
          setRegionFilter(value);
          trackEvent('pdm_filter_change', { filter: 'region', value });
        }}
      />

      <div className={styles.toolbarRow}>
        <Button appearance="subtle" icon={<ArrowDownloadRegular />} onClick={handleExport} disabled={filteredRows.length === 0}>
          Export to CSV
        </Button>
      </div>

      <div className={styles.filtersRow}>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel}>Search</label>
          <SearchBox
            placeholder="Partner name"
            value={searchInput}
            onChange={(_e, data) => {
              setSearchInput(data.value);
            }}
            style={{ minWidth: '240px' }}
          />
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="pdmFilter">PDM</label>
          <Select
            id="pdmFilter"
            value={pdmFilter}
            onChange={(_e, data) => {
              setPdmFilter(data.value);
              trackEvent('pdm_filter_change', { filter: 'pdm', value: data.value === ALL ? 'all' : 'specific' });
            }}
          >
            <option value={ALL}>All PDMs</option>
            <option value={UNASSIGNED_FILTER}>- Unassigned -</option>
            {pdmFilterOptions.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="regionFilter">Region</label>
          <Select
            id="regionFilter"
            value={regionFilter}
            onChange={(_e, data) => setRegionFilter(data.value)}
          >
            <option value={ALL}>All</option>
            {PDM_REGIONS.map((reg) => (
              <option key={reg} value={reg}>{reg}</option>
            ))}
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} htmlFor="assignmentFilter">Assignment</label>
          <Select
            id="assignmentFilter"
            value={assignmentFilter}
            onChange={(_e, data) => setAssignmentFilter(data.value as AssignmentFilter)}
          >
            <option value="All">All</option>
            <option value="Assigned">Assigned</option>
            <option value="Unassigned">Unassigned</option>
          </Select>
        </div>
        <div className={styles.filterGroup}>
          <label className={styles.filterLabel} aria-hidden="true">&nbsp;</label>
          <Button
            appearance="subtle"
            icon={<DismissRegular />}
            onClick={clearFilters}
            disabled={!hasActiveFilters}
          >
            Clear filters
          </Button>
        </div>
      </div>

      <div className={styles.resultsRow}>
        <Text size={200}>
          Showing {filteredRows.length} of {rows.length}
        </Text>
        <CoveragePill rows={rows} onShowUnassigned={showUnassignedFromPill} />
        {isLoading && rows.length > 0 && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Spinner size="extra-tiny" />
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>Updating…</Text>
          </span>
        )}
      </div>

      <div ref={tableWrapperRef} className={styles.tableWrapper}>
        {isLoading && rows.length === 0 ? (
          <Skeleton>
            {Array.from({ length: 8 }, (_, row) => (
              <div key={row} style={{ display: 'flex', gap: '16px', marginBottom: '12px' }}>
                {Array.from({ length: 8 }, (_, i) => (
                  <SkeletonItem key={i} style={{ width: '120px', height: '16px' }} />
                ))}
              </div>
            ))}
          </Skeleton>
        ) : filteredRows.length === 0 ? (
          <div className={styles.emptyState}>No partners match the current filters.</div>
        ) : (
          <DataGrid
            items={filteredRows}
            columns={columns}
            sortable
            resizableColumns
            resizableColumnsOptions={{ autoFitColumns: false }}
            columnSizingOptions={columnSizingOptions}
            defaultSortState={defaultSortState}
            size="medium"
            getRowId={(item) => item.partnerId}
          >
            <DataGridHeader>
              <DataGridRow>
                {({ renderHeaderCell }) => (
                  <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>
                )}
              </DataGridRow>
            </DataGridHeader>
            <DataGridBody<PartnerAssignmentRow>>
              {({ item, rowId }) => (
                <DataGridRow<PartnerAssignmentRow> key={rowId}>
                  {({ columnId }) => (
                    <DataGridCell>
                      <TableCellLayout>{renderCellContent(columnId as string, item)}</TableCellLayout>
                    </DataGridCell>
                  )}
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        )}
      </div>

      <RegionalLeadsEditorPanel
        open={regionalPanelOpen}
        onOpenChange={setRegionalPanelOpen}
        regions={PDM_REGIONS}
        groups={regionalLeadGroups}
        pdmPool={pdmPool}
        onSearch={searchDirectory}
        onAdd={handleAddRegionalLead}
        onRemove={handleRemoveRegionalLead}
        canEdit={canEdit}
        mutatingRegion={mutatingRegion}
        loadError={regionalLeadsError}
        loading={regionalLeadsLoading}
        onRetryLoad={retryRegionalLeads}
      />
    </div>
  );
};
